import React from 'react';

/**
 * ToothSVG Component - Renders a single tooth with 5 clickable surfaces
 * Surfaces: O (Occlusal/Incisal), M (Mesial), D (Distal), B (Buccal), L (Lingual)
 */
const ToothSVG = ({
  toothNumber,
  surfaces = {},
  onSurfaceClick,
  selectedSurface,
  size = 50,
  isPrimary = false,
  showNumber = true,
  disabled = false
}) => {
  // Scale factor for responsive sizing (reserved for future use)
  // eslint-disable-next-line no-unused-vars
  const scale = size / 50;

  // Determine if it's an anterior tooth (incisors/canines) or posterior (premolars/molars)
  const toothNum = parseInt(toothNumber.toString().slice(-1));
  const isAnterior = toothNum >= 1 && toothNum <= 3;

  // Surface colors based on conditions
  const getSurfaceColor = (surface) => {
    const data = surfaces[surface];
    if (data?.color) return data.color;
    return '#ffffff';
  };

  const getSurfaceSymbol = (surface) => {
    const data = surfaces[surface];
    return data?.symbol || null;
  };

  const handleClick = (surface) => {
    if (!disabled && onSurfaceClick) {
      onSurfaceClick(toothNumber, surface);
    }
  };

  const isSelected = (surface) => selectedSurface === surface;

  // Common styles
  const baseStroke = '#666';
  const hoverClass = disabled ? '' : 'cursor-pointer hover:opacity-80';
  // --brand-600. A literal rather than var(--brand-600) because this value is
  // written straight into an SVG `stroke` attribute, not a CSS declaration.
  const selectedStroke = '#2F6B58';
  const selectedStrokeWidth = 2;

  if (isAnterior) {
    // Anterior tooth (incisor/canine) - diamond-like shape
    return (
      <svg
        width={size}
        height={size * 1.2}
        viewBox="0 0 50 60"
        className="tooth-svg"
      >
        {/* Tooth number */}
        {showNumber && (
          <text
            x="25"
            y="58"
            textAnchor="middle"
            fontSize={isPrimary ? "8" : "10"}
            fill="#666"
            fontWeight="500"
          >
            {toothNumber}
          </text>
        )}

        {/* Lingual (L) - Top */}
        <path
          d="M 25 2 L 40 18 L 25 22 L 10 18 Z"
          fill={getSurfaceColor('L')}
          stroke={isSelected('L') ? selectedStroke : baseStroke}
          strokeWidth={isSelected('L') ? selectedStrokeWidth : 1}
          className={hoverClass}
          onClick={() => handleClick('L')}
        />
        {getSurfaceSymbol('L') && (
          <text x="25" y="14" textAnchor="middle" fontSize="8" fill="#333">{getSurfaceSymbol('L')}</text>
        )}

        {/* Mesial (M) - Left */}
        <path
          d="M 10 18 L 25 22 L 25 32 L 8 28 Z"
          fill={getSurfaceColor('M')}
          stroke={isSelected('M') ? selectedStroke : baseStroke}
          strokeWidth={isSelected('M') ? selectedStrokeWidth : 1}
          className={hoverClass}
          onClick={() => handleClick('M')}
        />
        {getSurfaceSymbol('M') && (
          <text x="15" y="26" textAnchor="middle" fontSize="8" fill="#333">{getSurfaceSymbol('M')}</text>
        )}

        {/* Incisal/Occlusal (O) - Center */}
        <path
          d="M 25 22 L 40 18 L 42 28 L 25 32 L 8 28 L 10 18 Z"
          fill={getSurfaceColor('O')}
          stroke={isSelected('O') ? selectedStroke : baseStroke}
          strokeWidth={isSelected('O') ? selectedStrokeWidth : 1}
          className={hoverClass}
          onClick={() => handleClick('O')}
        />
        {getSurfaceSymbol('O') && (
          <text x="25" y="26" textAnchor="middle" fontSize="8" fill="#333">{getSurfaceSymbol('O')}</text>
        )}

        {/* Distal (D) - Right */}
        <path
          d="M 40 18 L 25 22 L 25 32 L 42 28 Z"
          fill={getSurfaceColor('D')}
          stroke={isSelected('D') ? selectedStroke : baseStroke}
          strokeWidth={isSelected('D') ? selectedStrokeWidth : 1}
          className={hoverClass}
          onClick={() => handleClick('D')}
        />
        {getSurfaceSymbol('D') && (
          <text x="35" y="26" textAnchor="middle" fontSize="8" fill="#333">{getSurfaceSymbol('D')}</text>
        )}

        {/* Buccal (B) - Bottom */}
        <path
          d="M 8 28 L 25 32 L 42 28 L 38 45 L 25 48 L 12 45 Z"
          fill={getSurfaceColor('B')}
          stroke={isSelected('B') ? selectedStroke : baseStroke}
          strokeWidth={isSelected('B') ? selectedStrokeWidth : 1}
          className={hoverClass}
          onClick={() => handleClick('B')}
        />
        {getSurfaceSymbol('B') && (
          <text x="25" y="40" textAnchor="middle" fontSize="8" fill="#333">{getSurfaceSymbol('B')}</text>
        )}
      </svg>
    );
  }

  // Posterior tooth (premolar/molar) - square-like with 5 surfaces
  return (
    <svg
      width={size}
      height={size * 1.2}
      viewBox="0 0 50 60"
      className="tooth-svg"
    >
      {/* Tooth number */}
      {showNumber && (
        <text
          x="25"
          y="58"
          textAnchor="middle"
          fontSize={isPrimary ? "8" : "10"}
          fill="#666"
          fontWeight="500"
        >
          {toothNumber}
        </text>
      )}

      {/* Lingual (L) - Top */}
      <path
        d="M 10 5 L 40 5 L 35 15 L 15 15 Z"
        fill={getSurfaceColor('L')}
        stroke={isSelected('L') ? selectedStroke : baseStroke}
        strokeWidth={isSelected('L') ? selectedStrokeWidth : 1}
        className={hoverClass}
        onClick={() => handleClick('L')}
      />
      {getSurfaceSymbol('L') && (
        <text x="25" y="12" textAnchor="middle" fontSize="8" fill="#333">{getSurfaceSymbol('L')}</text>
      )}

      {/* Mesial (M) - Left */}
      <path
        d="M 10 5 L 15 15 L 15 35 L 10 45 L 5 25 Z"
        fill={getSurfaceColor('M')}
        stroke={isSelected('M') ? selectedStroke : baseStroke}
        strokeWidth={isSelected('M') ? selectedStrokeWidth : 1}
        className={hoverClass}
        onClick={() => handleClick('M')}
      />
      {getSurfaceSymbol('M') && (
        <text x="10" y="27" textAnchor="middle" fontSize="8" fill="#333">{getSurfaceSymbol('M')}</text>
      )}

      {/* Occlusal (O) - Center */}
      <path
        d="M 15 15 L 35 15 L 35 35 L 15 35 Z"
        fill={getSurfaceColor('O')}
        stroke={isSelected('O') ? selectedStroke : baseStroke}
        strokeWidth={isSelected('O') ? selectedStrokeWidth : 1}
        className={hoverClass}
        onClick={() => handleClick('O')}
      />
      {getSurfaceSymbol('O') && (
        <text x="25" y="27" textAnchor="middle" fontSize="10" fill="#333" fontWeight="bold">{getSurfaceSymbol('O')}</text>
      )}

      {/* Distal (D) - Right */}
      <path
        d="M 40 5 L 45 25 L 40 45 L 35 35 L 35 15 Z"
        fill={getSurfaceColor('D')}
        stroke={isSelected('D') ? selectedStroke : baseStroke}
        strokeWidth={isSelected('D') ? selectedStrokeWidth : 1}
        className={hoverClass}
        onClick={() => handleClick('D')}
      />
      {getSurfaceSymbol('D') && (
        <text x="40" y="27" textAnchor="middle" fontSize="8" fill="#333">{getSurfaceSymbol('D')}</text>
      )}

      {/* Buccal (B) - Bottom */}
      <path
        d="M 10 45 L 15 35 L 35 35 L 40 45 L 25 50 Z"
        fill={getSurfaceColor('B')}
        stroke={isSelected('B') ? selectedStroke : baseStroke}
        strokeWidth={isSelected('B') ? selectedStrokeWidth : 1}
        className={hoverClass}
        onClick={() => handleClick('B')}
      />
      {getSurfaceSymbol('B') && (
        <text x="25" y="44" textAnchor="middle" fontSize="8" fill="#333">{getSurfaceSymbol('B')}</text>
      )}
    </svg>
  );
};

/**
 * MissingTooth - Renders an X for extracted teeth
 */
export const MissingTooth = ({ toothNumber, size = 50, isPrimary = false }) => {
  return (
    <svg
      width={size}
      height={size * 1.2}
      viewBox="0 0 50 60"
      className="tooth-svg"
    >
      <line x1="10" y1="10" x2="40" y2="45" stroke="#95a5a6" strokeWidth="3" />
      <line x1="40" y1="10" x2="10" y2="45" stroke="#95a5a6" strokeWidth="3" />
      <text
        x="25"
        y="58"
        textAnchor="middle"
        fontSize={isPrimary ? "8" : "10"}
        fill="#95a5a6"
        fontWeight="500"
      >
        {toothNumber}
      </text>
    </svg>
  );
};

/**
 * ImplantTooth - Renders implant symbol
 */
export const ImplantTooth = ({ toothNumber, size = 50, isPrimary = false, color = '#9b59b6' }) => {
  return (
    <svg
      width={size}
      height={size * 1.2}
      viewBox="0 0 50 60"
      className="tooth-svg"
    >
      {/* Implant screw body */}
      <path
        d="M 20 8 L 30 8 L 28 40 L 22 40 Z"
        fill={color}
        stroke="#666"
        strokeWidth="1"
      />
      {/* Threads */}
      <line x1="20" y1="15" x2="30" y2="15" stroke="#fff" strokeWidth="1" />
      <line x1="21" y1="22" x2="29" y2="22" stroke="#fff" strokeWidth="1" />
      <line x1="21" y1="29" x2="29" y2="29" stroke="#fff" strokeWidth="1" />
      <line x1="22" y1="36" x2="28" y2="36" stroke="#fff" strokeWidth="1" />
      {/* Crown on top */}
      <ellipse cx="25" cy="8" rx="8" ry="4" fill="#f39c12" stroke="#666" strokeWidth="1" />
      <text
        x="25"
        y="58"
        textAnchor="middle"
        fontSize={isPrimary ? "8" : "10"}
        fill="#666"
        fontWeight="500"
      >
        {toothNumber}
      </text>
    </svg>
  );
};

export default ToothSVG;
